import { Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';
import { FormGroup, FormControl, Validators, ValidatorFn, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { FormFieldBase } from '../../classes/form-field-base';
import { FormFieldCheckbox } from '../../classes/form-field-checkbox'
import { Dictionary } from '../../classes/dictionary'
import { BrandService } from '../../services/brand/brand.service';
import { SessionAuthService } from '../../services/session/session-auth.service';
import { SharedService } from '../../services/shared.service';
import { DataExchangeService } from '../../services/session/data-exchange.service';
import { NotificationPreferencesService } from '../../services/notification/notification-preferences.service';
import { ComponentSectionFactoryService } from '../../services/component-section-factory.service';
import { HeaderSectionDirective } from '../../directives/header-section.directive';
import { FooterSectionDirective } from '../../directives/footer-section.directive';
import { DynamicFormFieldComponent } from '../section-blocks/dynamic-form-field/dynamic-form-field.component';
import { ThemeDirective } from '../../directives/theme.directive';
import { ISessionDetails } from '../../interfaces/session-details';

@Component({
    selector: 'app-notification-preferences',
    standalone: true,
    templateUrl: './notification-preferences.component.html',
    styleUrls: ['./notification-preferences.component.css'],
    imports: [
        ThemeDirective,
        HeaderSectionDirective,
        ReactiveFormsModule,
        DynamicFormFieldComponent,
        FooterSectionDirective
    ]
})
export class NotificationPreferencesComponent implements OnInit {

  @ViewChild(HeaderSectionDirective, {static: true}) headerComponentSection!: HeaderSectionDirective;
  @ViewChild(FooterSectionDirective, {static: true}) footerComponentSection!: FooterSectionDirective;

  brandStyleTheme: any = {};
  private readonly destroyRef = inject(DestroyRef);

  formFields: FormFieldBase<string>[] | null = [];
  form!: FormGroup;

  notifPrefsDict = new Dictionary();

  sessionId!: string;
  brandCode!: string;
  region!: string;
  serialNumber!: string;
  fieldId!: number;

  successMessage!: string;
  errorMessage!: string;
  isSuccessMessageShown: boolean = false;
  isErrorMessageShown: boolean = false;

  isComponentReady: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly sharedService: SharedService,
    private readonly dataExchangeService: DataExchangeService,
    private readonly sessionAuthService: SessionAuthService,
    // private walPageService: WalPageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly brandService: BrandService,
    private readonly componentSectionFactoryService: ComponentSectionFactoryService,
    private readonly notificationPreferencesService: NotificationPreferencesService) {
  }

  ngOnInit() {
    // Get the parameters from the caller (Walletron)
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.region = params.get('r') || '';
        this.sessionId = params.get('si') || '';

        // Get session details to find out the auth token
        this.sessionAuthService.getWalPageSession(this.region, this.sessionId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
        next: () => {
          // Retrieve account tasks session
          this.postInit();
        },
        error: (error) => {
          this.router.navigate(['/err']);
        }
      });

    });

  }

  postInit() {
   // Initialization of the form
    const fieldGroup: any = {};
    this.form = new FormGroup(fieldGroup);
    // Get session details
    this.brandCode = this.dataExchangeService.getBrandCode();
    console.log('brandCode', this.brandCode);
    // this.region    = this.dataExchangeService.getRegion();
    // console.log('region', this.region);
    const sessionDetails  = this.dataExchangeService.getSession();
    console.log('sessionDetails', sessionDetails);
    if (sessionDetails && sessionDetails.options) {
      const options: Map<string, any> = new Map(Object.entries(sessionDetails.options));
      if (options) {
        if (options.has("serialNumber")) {
          this.serialNumber = options.get('serialNumber') || '';
        }
        if (options.has("fieldId")) {
          this.fieldId = options.get('fieldId');
        }
        // Create form header and footer
        this.makeHeaderAndFooter();
        //  Retrieve customer's notification preferences
        // this.getNotificationPreferences();
        setTimeout(() => this.getNotificationPreferences(), 0); // Schedule to run after the current call stack is cleared

      }
    }

  }


  makeHeaderAndFooter() {
    this.brandService.getBrandInfo(this.region, this.brandCode, "NPE,WAL_PAGES")
    .subscribe(response => {
      if (response) {
        this.brandStyleTheme = this.sharedService.getBrandStyleScheme(response);
        response.sectionText = 'Manage Notification Preferences';
        this.componentSectionFactoryService.loadHeaderSection(response, this.headerComponentSection);
        this.componentSectionFactoryService.loadFooterSection(response, this.footerComponentSection);
      }
    });
  }


  getNotificationPreferences() {
    // Make a web api call
    this.notificationPreferencesService.getNotificationPreferences(this.region, this.serialNumber, this.fieldId)
    .subscribe(response => {
      this.formFields = [];
      if (response) {
        // Build dynamically notification preferences
        const fieldGroup: any = {};
        let validator: ValidatorFn[] = [Validators.required];
        response.forEach(element => {
          this.formFields?.push(new FormFieldCheckbox({
            key: element.notifName,
            label: element.shortDescription,
            checked:  element.defaultPref,
            required: true,
            order: 1,
            controlType: 'checkbox',
          }));
          fieldGroup[element.notifName] = validator.length > 0 ? new FormControl(element.defaultPref, validator)
                : new FormControl(element.defaultPref);
          this.notifPrefsDict.set(element.notifName, element.defaultPref);
        });
        this.form = new FormGroup(fieldGroup);
        this.isComponentReady = true;
      }
    });
  }

  onSubmit() {
    this.isSuccessMessageShown = false;
    this.isErrorMessageShown = false;
    this.notificationPreferencesService.modifyNotificationPreferences(
      this.region, this.serialNumber, this.fieldId, this.notifPrefsDict)
      .pipe(
        catchError(() => {
          this.errorMessage = 'An error occured.  Please call system administrator.';
          this.isErrorMessageShown = true;
          return EMPTY;
        })
      )
      .subscribe({
        next: (reponse) => {
          this.successMessage = 'Your notification preferences have been updated.';
          this.isSuccessMessageShown = true;
        },
        error: (err) => {
          this.errorMessage = 'An error occured.  Please call system administrator.';
          this.isErrorMessageShown = true;
        },
      });
  }

  onClose() {
    window.open('','_parent','');
    window.close();
  }

  changePreference(data: any) {
    if (this.notifPrefsDict.has(data.srcElement)) {
      this.notifPrefsDict.set(data.srcElement, data.checked)
    }
  }

}
